import {
  AfterViewInit,
  booleanAttribute,
  Directive,
  ElementRef,
  Input,
  NgZone,
  numberAttribute,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from "@angular/core";

/**
 * Sizes the host element to the space actually left below it, as a pixel height.
 *
 * This exists because the app's height chain is broken in a place CSS cannot reach. The shell IS a
 * correct flex column with a real height (`.main-content` -> `.page-content` ->
 * `.page-content-body`), but every Angular component element in between - `<app-some-screen>`,
 * `<unified-claimant-grid>`, `<grid>` - has no CSS rule at all and so defaults to `display: inline`.
 * A `flex: 1` below one of those is inert, which is why screens fall back to a `heightCalc-N` magic
 * number (`height: calc(100vh - 11rem) !important`) that has to guess how much chrome sits above the
 * element. The guess is wrong whenever the chrome is conditional - a card strip, an info banner, a
 * page-title row whose buttons wrap.
 *
 * The missing piece is one measurement, not a constant: how far from this element's top edge to the
 * nearest edge that genuinely clips. That is what this does.
 *
 * Nothing here is grid-specific. Use it on any element that should end where the visible area ends -
 * a scrolling panel body, a long form - not just on the grid.
 *
 * ```html
 * <div fillAvailableHeight class="overflow-auto">...</div>
 * <grid heightMode="fill" ...></grid>          <!-- how the grid opts in -->
 * ```
 *
 * Opt-in by design: a falsy `fillAvailableHeight` leaves the element completely alone (no observers,
 * no style written), so adding this directive to a shared component cannot move an existing screen.
 */
@Directive({
  selector: "[fillAvailableHeight]",
  standalone: true,
})
export class FillAvailableHeightDirective implements AfterViewInit, OnChanges, OnDestroy {
  /** Falsy = inert. A bare attribute (`fillAvailableHeight`) enables it. */
  @Input({ transform: booleanAttribute }) fillAvailableHeight = true;

  /**
   * Floor, in px. A short viewport gives back a scrollbar rather than a 40px-tall element -
   * deliberately: an unusable element is worse than a scrollbar.
   */
  @Input({ transform: numberAttribute }) fillMinHeight = 200;

  /** Extra breathing room to leave under the element, in px. */
  @Input({ transform: numberAttribute }) fillBottomGap = 0;

  /**
   * CSS selector pinning the boundary explicitly, for the rare host where auto-detection picks the
   * wrong ancestor. Normally leave this alone.
   */
  @Input() fillBoundary = "";

  private readonly host: HTMLElement;
  private resizeObservers: ResizeObserver[] = [];
  private mutationObserver: MutationObserver | null = null;
  private frame = 0;
  private appliedHeight = -1;
  private isRunning = false;

  constructor(element: ElementRef<HTMLElement>, private renderer: Renderer2, private zone: NgZone) {
    this.host = element.nativeElement;
  }

  ngAfterViewInit(): void {
    if (this.fillAvailableHeight) {
      this.start();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The first change lands before ngAfterViewInit, which owns the initial start().
    if (!changes["fillAvailableHeight"] || changes["fillAvailableHeight"].firstChange) {
      return;
    }
    if (this.fillAvailableHeight) {
      this.start();
    } else {
      this.stop();
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Force a re-measure, for a host that changes something the observers below cannot see. */
  public remeasure(): void {
    if (this.isRunning) {
      this.schedule();
    }
  }

  private start(): void {
    if (this.isRunning) {
      return;
    }
    this.isRunning = true;
    // Synchronously, not scheduled: ngAfterViewInit still runs before the browser paints, so the
    // element is never shown at its collapsed height for a frame.
    this.measure();
    this.zone.runOutsideAngular(() => this.observe());
  }

  private stop(): void {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    this.resizeObservers.forEach((observer) => observer.disconnect());
    this.resizeObservers = [];
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    window.removeEventListener("resize", this.onWindowResize);
    if (this.appliedHeight >= 0) {
      this.renderer.removeStyle(this.host, "height");
    }
    this.appliedHeight = -1;
    this.isRunning = false;
  }

  private observe(): void {
    const clips = this.clippingAncestors();

    // Viewport resize, left-nav collapse, a panel changing width and rewrapping its content.
    for (const clip of clips) {
      const observer = new ResizeObserver(this.onObservedChange);
      observer.observe(clip);
      this.resizeObservers.push(observer);
    }

    // The element itself - the cheapest signal for "became visible" (tab switch, panel open), where
    // every rect was 0 and measure() bailed. Our own write re-triggers this, which is harmless: the
    // measurement is anchored to the element's TOP edge, so the second pass computes the same number
    // and the >= 1px guard in measure() ends it there.
    const selfObserver = new ResizeObserver(this.onObservedChange);
    selfObserver.observe(this.host);
    this.resizeObservers.push(selfObserver);

    // `childList` only, and deliberately NOT `attributes`: our own style write would otherwise feed
    // straight back in as a mutation. This is the observer that catches content appearing ABOVE the
    // element once its data arrives - a card strip, an info banner - which is the case the
    // `heightCalc-N` constants could never handle.
    if (clips.length) {
      this.mutationObserver = new MutationObserver(this.onObservedChange);
      this.mutationObserver.observe(clips[0], { childList: true, subtree: true });
    }

    // Fallback for when the boundary IS the viewport and there is no element to observe.
    window.addEventListener("resize", this.onWindowResize, { passive: true });
  }

  private readonly onObservedChange = () => this.schedule();
  private readonly onWindowResize = () => this.schedule();

  /** Coalesce every signal into at most one measurement per frame. */
  private schedule(): void {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.measure();
    });
  }

  private measure(): void {
    if (!this.fillAvailableHeight) {
      return;
    }
    // Inside a closed panel or an inactive tab every rect is 0, so measuring would write nonsense.
    // The self-ResizeObserver above brings us back when it opens.
    if (!this.host.offsetParent) {
      return;
    }

    const clips = this.clippingAncestors();

    // The viewport is always a bound, and we take the MINIMUM over every clipping ancestor rather
    // than stopping at the nearest one. That matters on the older screens, where
    // `.page-content-body` is a fixed `calc(100vh - 11.7rem)` that can overhang `.page-content`:
    // trusting the nearest clip alone would size us past the visible area and leave an outer
    // ancestor scrolling, which is the very thing this is meant to prevent.
    let bottom = document.documentElement.clientHeight;
    for (const clip of clips) {
      const style = getComputedStyle(clip);
      const contentBottom =
        clip.getBoundingClientRect().bottom -
        (parseFloat(style.paddingBottom) || 0) -
        (parseFloat(style.borderBottomWidth) || 0);
      bottom = Math.min(bottom, contentBottom);
    }

    const available =
      bottom -
      this.host.getBoundingClientRect().top -
      this.reservedBelow(clips[0]) -
      this.fillBottomGap;
    const height = Math.max(Math.floor(available), this.fillMinHeight);

    // Only write on a real change. This is what stops the self-observing ResizeObserver above from
    // looping, so it is load-bearing, not an optimisation.
    if (Math.abs(height - this.appliedHeight) < 1) {
      return;
    }
    this.appliedHeight = height;
    // An inline height beats both `-webkit-fill-available` and the `!important` on the
    // `heightCalc-N` classes, so this needs no specificity games.
    this.renderer.setStyle(this.host, "height", `${height}px`);
  }

  /**
   * Ancestors that genuinely clip, nearest first.
   *
   * Only `auto` / `scroll` / `overlay` count. `hidden` and `clip` are deliberately ignored: they are
   * common on auto-height wrappers, whose bottom edge is set BY their content - i.e. by this element
   * - so trusting one would pin us to whatever height we already had. Every real `overflow-y: auto`
   * box in this app is a height-constrained one, and the viewport bound in measure() already covers
   * the `overflow: hidden` shell (`.main-content`).
   */
  private clippingAncestors(): HTMLElement[] {
    const found: HTMLElement[] = [];
    for (let node = this.host.parentElement; node && node !== document.body; node = node.parentElement) {
      if (this.fillBoundary) {
        if (node.matches(this.fillBoundary)) {
          found.push(node);
          break;
        }
        continue;
      }
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
        found.push(node);
      }
    }
    return found;
  }

  /**
   * Height to leave for anything that sits BELOW this element, at every level up to the boundary:
   * following siblings (plus the parent's row gap), each intermediate parent's bottom padding and
   * border, and the element's own bottom margin.
   *
   * This is what keeps a footer under the element on screen - the "Other items in this report" bar
   * on the report-detail screens, the `import-footer` on the universal-import screens - instead of
   * pushing it out of view.
   */
  private reservedBelow(boundary: HTMLElement | undefined): number {
    let reserved = 0;
    let node: HTMLElement = this.host;
    while (node.parentElement) {
      const parent = node.parentElement;
      const parentStyle = getComputedStyle(parent);
      // `normal` on a non-flex/grid parent parses to NaN, i.e. no gap. Which is correct.
      const rowGap = parseFloat(parentStyle.rowGap) || 0;

      reserved += parseFloat(getComputedStyle(node).marginBottom) || 0;
      for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
        const rect = sibling.getBoundingClientRect();
        if (rect.height <= 0 || this.isOutOfFlow(sibling)) {
          continue;
        }
        reserved += rect.height + rowGap;
      }

      // Stop AFTER counting the boundary's own children, but before its bottom padding - measure()
      // has already taken that off the boundary's content-box bottom.
      if (parent === boundary || parent === document.body) {
        break;
      }
      reserved +=
        (parseFloat(parentStyle.paddingBottom) || 0) +
        (parseFloat(parentStyle.borderBottomWidth) || 0);
      node = parent;
    }
    return reserved;
  }

  /**
   * A `position: fixed` / `absolute` sibling takes up no room in the flow, so reserving space for it
   * would shrink the element for nothing.
   *
   * This is not hypothetical: components in this app routinely declare their side panels as plain
   * siblings of their content (`<app-action-panel-new>`, `<app-fx-rate-history>` after the grid in
   * `fx-rate.component.html`), and `.ds-side__panel` is `position: fixed`. Without this, opening a
   * panel would visibly shorten the grid behind it.
   */
  private isOutOfFlow(element: Element): boolean {
    const position = getComputedStyle(element).position;
    return position === "fixed" || position === "absolute";
  }
}
